<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {ElOption, ElSelect} from 'element-plus';
import {createObject, deleteObject, listObjects, updateObject} from '@/base/callBackend';
import {showHideLoading} from '@/base/loadingState';
import {CabFinancieleRegel, CabOrganization, uuidOf} from 'common';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import {FINANCIELE_REGEL_CHANGE_EMIT, FINANCIELE_REGEL_REMOVE_EMIT} from '@/base/cabConstants';
import FinancieleRegelStrip from '@/views/admin/FinancieleRegelStrip.vue';
import PlusButton from '@/views/icons/PlusIcon.vue';
import GeneralFilter from '@/views/general/GeneralFilter.vue';

const alle          = ref<CabFinancieleRegel[]>([]);
const organizations = ref<CabOrganization[]>([]);
const kwartaal      = ref<string>('');
const filterActive  = ref<boolean>(false);
const vanKwartaal   = computed<CabFinancieleRegel[]>(() => alle.value.filter(i => kwartaal.value === '' || i.kwartaal === kwartaal.value));
const gefilterd     = ref<CabFinancieleRegel[]>([]);
const kwartalen     = computed<string[]>(() => [...new Set(alle.value.map(i => i.kwartaal))].sort().reverse());

onMounted(async () => {
    showHideLoading(true);
    try {
        organizations.value = await listObjects(CabOrganization);
        alle.value          = await listObjects(CabFinancieleRegel);
        kwartaal.value      = kwartalen.value[0] ?? '';
    } finally {
        showHideLoading(false);
    }
});

//=====================================================================================
async function onRemove(regel: CabFinancieleRegel) {
    await deleteObject(regel);
    alle.value = alle.value.filter((i) => uuidOf(i) !== uuidOf(regel));
}

async function onAdd() {
    filterActive.value              = false;
    const nieuw: CabFinancieleRegel = new CabFinancieleRegel();
    nieuw.kwartaal                  = kwartaal.value;
    await createObject(nieuw);
    alle.value = [nieuw, ...alle.value];
}

async function onChange(regel: CabFinancieleRegel) {
    const uuid: string = uuidOf(regel);
    await updateObject(regel);
    alle.value = alle.value.map((i) => uuidOf(i) === uuid ? regel : i);
}
</script>

<template>
    <div class="flex flex-col flex-1">
        <div class="flex flex-row flex-1 mb-1 items-center gap-2">
            <PlusButton @click="onAdd()"/>
            <div class="text-2xl flex-1 ml-2" data-testid="data-section-title">
                Financien
            </div>
            <el-select v-model="kwartaal" placeholder="kwartaal" class="w-32" clearable>
                <el-option v-for="q in kwartalen" :key="q" :value="q" :label="q"/>
            </el-select>
            <GeneralFilter v-model:active="filterActive"
                           :cab-list="vanKwartaal"
                           v-model:cab-list-filtered="gefilterd"
                           :id="'FinancieleRegelList'"
            />
        </div>
        <div class="text-sm text-gray-500 mb-2">
            Een rij per organisatie, thema, kostenpost, categorie en kwartaal, zoals in het IZA-bestand. Nieuwe rijen komen binnen via Upload.
        </div>
        <FinancieleRegelStrip
            v-for="regel in gefilterd"
            :key="uuidOf(regel)"
            :regel="regel"
            :allOrganizations="organizations"
            v-on:[FINANCIELE_REGEL_REMOVE_EMIT]="onRemove"
            v-on:[FINANCIELE_REGEL_CHANGE_EMIT]="onChange"
        />
    </div>
</template>
